import React, { useMemo } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useClient } from '../contexts/ClientContext';
import { useCompanyFeatures } from '../contexts/CompanyFeaturesContext';
import { canSeeNavItem, getVisibleNavItems, NavItem } from '../utils/navVisibility';
import { useParams } from 'react-router-dom';
import { getActiveCompanySlug } from '../utils/companySlug';
import { isSuperadminPath } from '../utils/routeHelpers';

// This matches the items from Sidebar.tsx
const ALL_NAV_ITEMS: NavItem[] = [
  // Client
  { to: '/home', text: 'ראשי', roles: ['client'] },
  { to: '/appointments', text: 'תורים', roles: ['client'] },
  { to: '/calendar', text: 'יומן גרפי', roles: ['client'] },
  { to: '/my-profile', text: 'פרופיל', roles: ['client'] },
  { to: '/my-orders', text: 'הזמנות שלי', roles: ['client'] },

  // Agent
  { to: '/home', text: 'ראשי', roles: ['agent'] },
  { to: '/agents', text: 'פוטנציאל', roles: ['agent'], feature: 'agents' },
  { to: '#sticky-notes', text: 'פתקים', roles: ['agent'], feature: 'sticky-notes' },
  { to: '#calculator', text: 'מחשבון', roles: ['agent'], feature: 'calculator' },
  { to: '/agent/settings', text: 'הגדרות', roles: ['agent'], feature: 'settings' },

  // Superadmin
  { to: '/superadmin/home', text: 'ראשי', roles: ['superadmin'] },
  { to: '/superadmin/infrastructure', text: 'סטטיסטיקות תשתית', roles: ['superadmin'] },
  { to: '/superadmin/announcements', text: 'הודעות וכותרות', roles: ['superadmin'] },
  { to: '/superadmin/logs', text: 'לוגי שרת', roles: ['superadmin'] },
  { to: '/superadmin/online-users', text: 'משתמשים מחוברים', roles: ['superadmin'] },
  { to: '/superadmin/companies', text: 'ניהול חברות', roles: ['superadmin'] },
  { to: '/superadmin/company-features', text: 'כללי חברה ותכונות', roles: ['superadmin'], feature: 'company-features' },
  { to: '#lumira-ai', text: 'Lumira AI', roles: ['superadmin'], feature: 'lumira-ai' },
  { to: '/admin/settings', text: 'הודעות SMS', roles: ['superadmin'], feature: 'sms' },

  // Admin
  { to: '/home', text: 'ראשי', roles: ['admin', 'manager', 'client'] },
  { to: '/dashboard', text: 'לוח ניהול', roles: ['admin'], feature: 'dashboard' },
  { to: '/clients', text: 'לקוחות', roles: ['admin'], feature: 'clients' },
  { to: '/managers', text: 'משתמשים', roles: ['admin'], feature: 'managers' },
  { to: '/agents', text: 'סוכנים', roles: ['admin'], feature: 'agents' },
  { to: '/leads', text: 'לידים', roles: ['admin'], feature: 'leads' },
  { to: '/admin/orders', text: 'הזמנות', roles: ['admin'], feature: 'orders' },
  { to: '/admin/proposals', text: 'הצעות מחיר', roles: ['admin'] },
  { to: '/admin/inventory', text: 'ניהול מלאי', roles: ['admin'], feature: 'inventory' },
  { to: '/admin/slots', text: 'ניהול תורים', roles: ['admin'], feature: 'slots' },
  { to: '/admin/tasks', text: 'משימות', roles: ['admin'], feature: 'tasks' },
  { to: '/admin/calendar', text: 'לוח שנה', roles: ['admin'], feature: 'calendar' },
  { to: '/admin/automations', text: 'אוטומציות', roles: ['admin'], feature: 'automations' },
  { to: '#sticky-notes', text: 'פתקים', roles: ['admin'], feature: 'sticky-notes' },
  { to: '#calculator', text: 'מחשבון', roles: ['admin'], feature: 'calculator' },
  { to: '#chat', text: 'צ\'אט', roles: ['admin', 'manager', 'agent'], feature: 'chat' },
  { to: '/admin/settings', text: 'הגדרות', roles: ['admin'], feature: 'settings' },
  { to: '/bug-wall', text: 'קיר הבאגים', roles: ['admin'] },

  // Manager
  { to: '/home', text: 'ראשי', roles: ['manager'] },
  { to: '/manager-crm', text: 'לקוחות שלי', roles: ['manager'] },
  { to: '/dashboard', text: 'לוח ניהול', roles: ['manager'], permAll: ['overview'], feature: 'dashboard' },
  { to: '/clients', text: 'ניהול לקוחות', roles: ['manager'], permAll: ['manage_clients'], feature: 'clients' },
  { to: '/leads/my', text: 'לידים', roles: ['manager'], permAny: ['view_leads'], feature: 'leads' },
  { to: '/admin/orders', text: 'הזמנות', roles: ['manager'], permAll: ['manage_orders'], feature: 'orders' },
  { to: '/admin/proposals', text: 'הצעות מחיר', roles: ['manager'], permAny: ['view_proposals'] },
  { to: '/manager/tasks', text: 'משימות', roles: ['manager'], permAll: ['view_tasks'], feature: 'tasks' },
  { to: '/managers', text: 'משתמשים', roles: ['manager'], permAll: ['view_manager'], feature: 'managers' },
  { to: '/agents', text: 'סוכנים', roles: ['manager'], permAny: ['view_agent', 'view_potential_clients'], feature: 'agents' },
  { to: '/admin/slots', text: 'ניהול תורים', roles: ['manager'], permAll: ['view_slots'], feature: 'slots' },
  { to: '#sticky-notes', text: 'פתקים', roles: ['manager'], feature: 'sticky-notes' },
  { to: '#calculator', text: 'מחשבון', roles: ['manager'], feature: 'calculator' },
  { to: '/manager/settings', text: 'הגדרות', roles: ['manager'], feature: 'settings' },

  // Common
  { to: '/issues', text: 'תמיכה', roles: ['admin', 'manager', 'agent', 'client'], feature: 'issues' },
  { to: '/about', text: 'אודות', feature: 'about' },
];

interface SidebarRouteGuardProps {
  children: React.ReactNode;
}

/**
 * Guard component that checks if the current route is visible in the sidebar
 * If not, redirects to NoPermission page
 */
export default function SidebarRouteGuard({ children }: SidebarRouteGuardProps) {
  const location = useLocation();
  const { company } = useParams<{ company: string }>();
  const { client } = useClient();
  const { hasFeature, hiddenFromSidebar } = useCompanyFeatures();
  
  // CRITICAL: All hooks must be called before any early returns
  const role = client?.role;
  const permissions = client?.permissions || [];
  const companySlug = company || getActiveCompanySlug();
  
  // CRITICAL: Skip company route guard logic for superadmin routes
  // But we still need to call hooks first (Rules of Hooks)
  const isSuperadmin = isSuperadminPath(location.pathname);
  
  // CRITICAL: Prevent using undefined company slug
  const isValidCompany = companySlug && companySlug !== 'undefined';
  
  const base = isValidCompany ? `/${companySlug}` : '';

  // Get current path without company prefix
  const currentPath = isValidCompany ? (location.pathname.replace(`/${companySlug}`, '') || '/home') : location.pathname;
  
  // Normalize hash routes
  const normalizedPath = currentPath.startsWith('#') ? currentPath : currentPath;

  // Get visible nav items (always call hooks, even for superadmin)
  const visibleItems = useMemo(() => {
    return getVisibleNavItems(ALL_NAV_ITEMS, role, permissions, hasFeature, hiddenFromSidebar, client);
  }, [role, permissions, hasFeature, hiddenFromSidebar, client]);

  // Check if current route is in visible items
  const isRouteVisible = useMemo(() => {
    // For superadmin routes, always allow (they don't use company-based route guarding)
    if (isSuperadmin) {
      return true;
    }
    
    // Check exact match
    const exactMatch = visibleItems.some(item => {
      const itemPath = item.to.startsWith('#') ? item.to : item.to.replace(base, '');
      return itemPath === normalizedPath || itemPath === currentPath;
    });

    if (exactMatch) return true;

    // Check if it's a dynamic route (e.g., /client/:id, /admin/manager/:id)
    // For dynamic routes, check if the base route is visible
    const baseRoute = normalizedPath.split('/').slice(0, 3).join('/'); // e.g., /client/123 -> /client
    const baseMatch = visibleItems.some(item => {
      const itemPath = item.to.startsWith('#') ? item.to : item.to.replace(base, '');
      return itemPath === baseRoute || normalizedPath.startsWith(itemPath + '/');
    });

    return baseMatch;
  }, [visibleItems, normalizedPath, currentPath, base, isSuperadmin]);

  // Allow access to home, login, and settings pages
  const allowedPaths = ['/home', '/login', '/register', '/my-profile', '/client/settings', '/manager/settings', '/agent/settings', '/admin/settings'];
  const isAllowedPath = allowedPaths.some(path => currentPath === path || currentPath.startsWith(path));

  // Special handling for dynamic routes (e.g., /client/:id, /admin/manager/:id)
  // Check if the base route pattern is visible
  const isDynamicRouteVisible = useMemo(() => {
    // For superadmin routes, skip dynamic route checking
    if (isSuperadmin) {
      return false;
    }
    
    // Patterns for dynamic routes
    const dynamicPatterns = [
      { pattern: /^\/client\/.+/, base: '/clients' },
      { pattern: /^\/admin\/manager\/.+/, base: '/managers' },
      { pattern: /^\/admin\/slots\/.+/, base: '/admin/slots' },
      { pattern: /^\/admin\/proposals\/.+/, base: '/admin/proposals' },
    ];

    for (const { pattern, base } of dynamicPatterns) {
      if (pattern.test(currentPath)) {
        return visibleItems.some(item => {
          const itemPath = item.to.startsWith('#') ? item.to : item.to.replace(base, '');
          return itemPath === base || itemPath === base.replace(`/${companySlug}`, '');
        });
      }
    }
    return false;
  }, [currentPath, visibleItems, base, companySlug, isSuperadmin]);

  // CRITICAL: Early return for superadmin routes (after all hooks are called)
  if (isSuperadmin) {
    // Superadmin routes don't need company-based route guarding
    return <>{children}</>;
  }
  
  // For company routes, validate company slug
  if (!isValidCompany) {
    console.warn('[SidebarRouteGuard] Invalid companySlug, allowing access:', {
      pathname: location.pathname,
      companySlug,
    });
    return <>{children}</>;
  }

  if (isAllowedPath || isRouteVisible || isDynamicRouteVisible) {
    return <>{children}</>;
  }

  // Route not visible in sidebar - redirect to home
  // CRITICAL: Ensure companySlug is valid before building redirect path
  // (isValidCompany is already checked above, but double-check for safety)
  if (!isValidCompany || !companySlug) {
    console.warn('[SidebarRouteGuard] Cannot redirect to home - invalid companySlug:', companySlug);
    return <>{children}</>;
  }
  
  return <Navigate to={`/${companySlug}/home`} replace />;
}

